// Model graph explorer. Internal tool, so it optimises for density and for
// answering engineering questions fast: what feeds this, what breaks if I
// change it, and what is actually guarding it.
//
// Layout: a collapsible rail of every table, a stat strip, then two tabs -
// the lineage canvas and the test catalogue for the selected model.
//
// "Show every step" reveals the stg_/int_ plumbing. With it off those nodes
// are BRIDGED, not dropped: hidden nodes are traversed through so Orders
// still connects Raw to Cleansing to Decision-Ready. Naively filtering them
// would sever the chain.

import { useEffect, useMemo, useState } from "react";
import type { LineageGraph, LineageGraphNode } from "../types";
import { LineageCanvas, type CanvasNode } from "./LineageCanvas";
import { Search as SearchIcon } from "./LineageIcons";
import { TestCatalogue } from "./TestCatalogue";
import { QueryPanel } from "./QueryPanel";

const LAYER_LABEL: Record<string, string> = {
  bronze: "Raw Ingestion",
  silver: "Cleansing",
  gold: "Decision-Ready",
};
const LAYER_ORDER = ["bronze", "silver", "gold"];

type Adjacency = Map<string, string[]>;

interface Maps {
  byId: Map<string, LineageGraphNode>;
  up: Adjacency;
  down: Adjacency;
}

function buildMaps(graph: LineageGraph, showInternal: boolean): Maps {
  const byId = new Map(graph.nodes.map((n) => [n.id, n]));
  const rawUp: Adjacency = new Map();
  const rawDown: Adjacency = new Map();
  const push = (adj: Adjacency, key: string, value: string) => {
    const list = adj.get(key);
    if (list) list.push(value);
    else adj.set(key, [value]);
  };
  for (const e of graph.edges) {
    if (!byId.has(e.from) || !byId.has(e.to)) continue;
    push(rawDown, e.from, e.to);
    push(rawUp, e.to, e.from);
  }
  if (showInternal) return { byId, up: rawUp, down: rawDown };

  const hidden = (id: string) => byId.get(id)?.internal === true;
  const resolve = (start: string, adj: Adjacency): string[] => {
    const out = new Set<string>();
    const stack = [...(adj.get(start) ?? [])];
    const seen = new Set<string>();
    while (stack.length) {
      const id = stack.pop()!;
      if (seen.has(id)) continue;
      seen.add(id);
      if (hidden(id)) stack.push(...(adj.get(id) ?? []));
      else out.add(id);
    }
    return [...out];
  };

  const up: Adjacency = new Map();
  const down: Adjacency = new Map();
  for (const n of graph.nodes) {
    if (n.internal) continue;
    up.set(n.id, resolve(n.id, rawUp));
    down.set(n.id, resolve(n.id, rawDown));
  }
  return { byId, up, down };
}

/** Breadth-first walk collecting every reachable node and its hop distance. */
function walk(
  startId: string,
  adj: Adjacency,
  direction: "up" | "down",
): Map<string, { hops: number; direction: "up" | "down" }> {
  const out = new Map<string, { hops: number; direction: "up" | "down" }>();
  let frontier = [startId];
  let hops = 0;
  const seen = new Set([startId]);
  while (frontier.length && hops < 8) {
    hops += 1;
    const next: string[] = [];
    for (const id of frontier) {
      for (const nb of adj.get(id) ?? []) {
        if (seen.has(nb)) continue;
        seen.add(nb);
        out.set(nb, { hops, direction });
        next.push(nb);
      }
    }
    frontier = next;
  }
  return out;
}

export function TableLineageExplorer({
  graph,
  runState,
}: {
  graph: LineageGraph;
  runState?: Record<string, string>;
}) {
  const [showInternal, setShowInternal] = useState(false);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const [tab, setTab] = useState<"graph" | "query" | "tests">("graph");
  const [full, setFull] = useState(false);
  const [railOpen, setRailOpen] = useState(true);
  // Rail sections start open; the reader collapses the layers they are not
  // working in rather than being handed a pre-guessed state.
  const [openLayers, setOpenLayers] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(LAYER_ORDER.map((l) => [l, true])),
  );

  const maps = useMemo(() => buildMaps(graph, showInternal), [graph, showInternal]);

  const visibleNodes = useMemo(
    () =>
      graph.nodes
        .filter((n) => showInternal || !n.internal)
        .sort(
          (a, b) =>
            LAYER_ORDER.indexOf(a.layer) - LAYER_ORDER.indexOf(b.layer) ||
            a.label.localeCompare(b.label),
        ),
    [graph, showInternal],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return visibleNodes;
    return visibleNodes.filter(
      (n) => n.label.toLowerCase().includes(q) || n.name.toLowerCase().includes(q),
    );
  }, [visibleNodes, query]);

  const current =
    (selected && maps.byId.get(selected)) ||
    visibleNodes.find((n) => n.name === "gold_orders") ||
    filtered[0] ||
    null;

  const sub = useMemo(() => {
    if (!current) {
      return {
        nodes: [] as CanvasNode[],
        edges: [] as { from: string; to: string }[],
        up: 0,
        down: 0,
      };
    }
    const ups = walk(current.id, maps.up, "up");
    const downs = walk(current.id, maps.down, "down");
    const nodes: CanvasNode[] = [{ ...current, hops: 0, direction: "self" }];
    for (const [id, meta] of [...ups, ...downs]) {
      const n = maps.byId.get(id);
      if (n) nodes.push({ ...n, hops: meta.hops, direction: meta.direction });
    }
    const ids = new Set(nodes.map((n) => n.id));
    const edges: { from: string; to: string }[] = [];
    for (const n of nodes) {
      for (const d of maps.down.get(n.id) ?? []) {
        if (ids.has(d)) edges.push({ from: n.id, to: d });
      }
    }
    return { nodes, edges, up: ups.size, down: downs.size };
  }, [current, maps]);

  // Escape leaves fullscreen. Without this the only way out is the button,
  // which is a trap the moment the pointer is somewhere else.
  useEffect(() => {
    if (!full) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setFull(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [full]);

  const searchActive = query.trim().length > 0;

  return (
    <div
      className={`tlx${full ? " tlx-full" : ""}${railOpen ? "" : " tlx-railclosed"}`}
    >
      {/* Collapsing the whole rail buys the graph ~250px, which is the
          difference between a wide chain fitting and not. */}
      <button
        className="tlx-railtoggle"
        onClick={() => setRailOpen((v) => !v)}
        aria-expanded={railOpen}
        title={railOpen ? "Hide table list" : "Show table list"}
      >
        <span className={`tlx-caret${railOpen ? " is-open" : ""}`} aria-hidden="true" />
        {railOpen ? "Hide list" : `Tables (${visibleNodes.length})`}
      </button>
      <aside className="tlx-rail" hidden={!railOpen}>
        <div className="tlx-search-wrap">
          <SearchIcon className="tlx-search-icon" />
          <input
            className="tlx-search"
            type="search"
            placeholder="Search tables"
            aria-label="Search tables"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <label className="tlx-toggle">
          <input
            type="checkbox"
            checked={showInternal}
            onChange={(e) => setShowInternal(e.target.checked)}
          />
          <span>Show every step</span>
          <span className="tlx-toggle-note">
            {showInternal ? graph.nodes.length : visibleNodes.length}
          </span>
        </label>

        <div className="tlx-groups">
          {LAYER_ORDER.map((layer) => {
            const group = filtered.filter((n) => n.layer === layer);
            if (!group.length) return null;
            // A search forces every matching section open - collapsing a
            // section that contains a hit would hide the result.
            const open = searchActive || openLayers[layer];
            return (
              <div className="tlx-group" key={layer}>
                <button
                  className="tlx-group-head"
                  onClick={() =>
                    setOpenLayers((prev) => ({ ...prev, [layer]: !prev[layer] }))
                  }
                  aria-expanded={open}
                >
                  <span className={`tlx-caret${open ? " is-open" : ""}`} aria-hidden="true" />
                  <span className={`lcv-coldot lcv-coldot-${layer}`} />
                  <span className="tlx-group-name">{LAYER_LABEL[layer] ?? layer}</span>
                  <span className="tlx-group-count">{group.length}</span>
                </button>
                {open &&
                  group.map((n) => (
                    <button
                      key={n.id}
                      className={`tlx-item${current?.id === n.id ? " active" : ""}`}
                      onClick={() => setSelected(n.id)}
                    >
                      <span className="tlx-item-label">{n.label}</span>
                      <span className="tlx-item-row">
                        <code className="tlx-item-name">{n.name}</code>
                        {n.test_count > 0 ? (
                          <span className="tlx-item-tests">{n.test_count}</span>
                        ) : (
                          <span className="tlx-item-tests is-none" title="No tests">
                            0
                          </span>
                        )}
                      </span>
                    </button>
                  ))}
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="tlx-norail">No table matches that search.</div>
          )}
        </div>
      </aside>

      <section className="tlx-detail">
        {current && (
          <>
            <header className="tlx-head">
              <div className="tlx-head-top">
                <h3>{current.label}</h3>
                <span className={`tlx-badge tlx-badge-${current.layer}`}>
                  {LAYER_LABEL[current.layer] ?? current.layer}
                </span>
                {current.internal && (
                  <span className="tlx-badge tlx-badge-int">Internal step</span>
                )}
                <code className="tlx-tech">{current.id}</code>
              </div>
              {current.description && <p className="tlx-desc">{current.description}</p>}
            </header>

            <div className="tlx-stats">
              <Stat
                value={sub.up}
                label={sub.up === 1 ? "table feeds it" : "tables feed it"}
                tone="up"
              />
              <Stat
                value={sub.down}
                label={sub.down === 1 ? "table built from it" : "tables built from it"}
                tone="down"
              />
              <Stat
                value={maps.up.get(current.id)?.length ?? 0}
                label={
                  (maps.up.get(current.id)?.length ?? 0) === 1
                    ? "direct input"
                    : "direct inputs"
                }
                tone="plain"
              />
              <Stat
                value={current.test_count}
                label={current.test_count === 1 ? "test guards it" : "tests guard it"}
                tone={current.test_count === 0 ? "warn" : "plain"}
              />
            </div>

            <div className="tlx-tabbar">
              <div className="tlx-tabs" role="tablist">
                <button
                  role="tab"
                  aria-selected={tab === "graph"}
                  className={tab === "graph" ? "active" : ""}
                  onClick={() => setTab("graph")}
                >
                  Lineage
                </button>
                <button
                  role="tab"
                  aria-selected={tab === "query"}
                  className={tab === "query" ? "active" : ""}
                  onClick={() => setTab("query")}
                >
                  Query
                </button>
                <button
                  role="tab"
                  aria-selected={tab === "tests"}
                  className={tab === "tests" ? "active" : ""}
                  onClick={() => setTab("tests")}
                >
                  Tests
                  <span className="tlx-tabcount">{current.test_count}</span>
                </button>
              </div>
              {tab === "graph" && (
                <button
                  className="tlx-fullbtn"
                  onClick={() => setFull((v) => !v)}
                  aria-pressed={full}
                >
                  {full ? "Exit full screen" : "Full screen"}
                  {full && <span className="tlx-esc">Esc</span>}
                </button>
              )}
            </div>

            {tab === "graph" && (
              <LineageCanvas
                nodes={sub.nodes}
                edges={sub.edges}
                focusId={current.id}
                onSelect={setSelected}
                tall={full}
                runState={runState}
              />
            )}
            {tab === "query" && <QueryPanel node={current} />}
            {tab === "tests" && <TestCatalogue node={current} />}
          </>
        )}
      </section>
    </div>
  );
}

function Stat({
  value,
  label,
  tone,
}: {
  value: number;
  label: string;
  tone: "up" | "down" | "plain" | "warn";
}) {
  return (
    <div className={`tlx-stat tlx-stat-${tone}`}>
      <div className="tlx-stat-value">{value}</div>
      <div className="tlx-stat-label">{label}</div>
    </div>
  );
}
